// Version compatibility checker for WobotRelay Frontend
// Import from api.js to keep configuration centralized
import { API_BASE_URL } from './api';

export const FRONTEND_VERSION = process.env.REACT_APP_FRONTEND_VERSION || "1.0.0";
export const API_VERSION = parseInt(process.env.REACT_APP_API_VERSION || "2", 10);
export const REQUIRED_API_VERSION = API_VERSION;

let compatibilityCache = null;
let lastCheckTime = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Fetches compatibility information from the backend /meta endpoint
 * @returns {Promise<Object>} Compatibility matrix from backend
 */
export const fetchCompatibilityInfo = async () => {
    const now = Date.now();
    
    // Return cached data if still valid
    if (compatibilityCache && (now - lastCheckTime) < CACHE_TTL) {
        return compatibilityCache;
    }
    
    try {
        const response = await fetch(`${API_BASE_URL}/meta`);
        
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        compatibilityCache = await response.json();
        lastCheckTime = now;
        return compatibilityCache;
    } catch (error) {
        console.error('Failed to fetch compatibility info:', error);
        throw error;
    }
};

/**
 * Simple semver comparison (supports x.y.z format)
 * Returns: -1 if v1 < v2, 0 if equal, 1 if v1 > v2
 */
const compareSemver = (v1, v2) => {
    const parts1 = v1.split('.').map(Number);
    const parts2 = v2.split('.').map(Number);
    
    for (let i = 0; i < 3; i++) {
        const a = parts1[i] || 0;
        const b = parts2[i] || 0;
        if (a < b) return -1;
        if (a > b) return 1;
    }
    return 0;
};

/**
 * Checks if current frontend version is compatible with backend
 * @returns {Promise<Object>} { compatible: boolean, reason: string, details: object }
 */
export const checkCompatibility = async () => {
    try {
        const meta = await fetchCompatibilityInfo();
        
        // Check API version compatibility
        if (meta.backend.apiVersion !== REQUIRED_API_VERSION) {
            return {
                compatible: false,
                reason: `API version mismatch: Frontend requires API v${REQUIRED_API_VERSION}, but backend provides v${meta.backend.apiVersion}`,
                details: meta,
                severity: 'error'
            };
        }
        
        // Check frontend version range
        const minVersion = meta.supports.frontend.min;
        const maxVersion = meta.supports.frontend.max;
        
        if (compareSemver(FRONTEND_VERSION, minVersion) < 0) {
            return {
                compatible: false,
                reason: `Frontend version ${FRONTEND_VERSION} is too old. Minimum required: ${minVersion}`,
                details: meta,
                severity: 'error',
                action: 'upgrade'
            };
        }
        
        if (compareSemver(FRONTEND_VERSION, maxVersion) >= 0) {
            return {
                compatible: false,
                reason: `Frontend version ${FRONTEND_VERSION} is too new. Maximum supported: ${maxVersion}`,
                details: meta,
                severity: 'error',
                action: 'downgrade_or_wait'
            };
        }
        
        // All checks passed
        return {
            compatible: true,
            reason: 'Version compatibility verified',
            details: meta,
            severity: 'success'
        };
        
    } catch (error) {
        return {
            compatible: null, // Unknown state
            reason: `Failed to check compatibility: ${error.message}`,
            details: null,
            severity: 'warning'
        };
    }
};

/**
 * Displays compatibility warning/error in the UI
 * @param {Object} result - Result from checkCompatibility()
 */
export const displayCompatibilityStatus = (result) => {
    if (!result) return;
    
    const messages = {
        error: '🚫',
        warning: '⚠️',
        success: '✅'
    };
    
    const icon = messages[result.severity] || 'ℹ️';
    
    console.log(`${icon} Compatibility Check: ${result.reason}`);
    
    if (result.details) {
        console.log('Backend Version:', result.details.backend.version);
        console.log('Backend API Version:', result.details.backend.apiVersion);
        console.log('Frontend Version:', FRONTEND_VERSION);
        console.log('Required API Version:', REQUIRED_API_VERSION);
    }
    
    return result;
};
